import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Count from "./Count";
import AlarmFilters from "./Filters";
import List from "./List";
import Trend from "./Trend";
import { useGetDevicesQuery } from "services/devices";
import Dragable from "components/Dragable";
import Tooltip from "@mui/material/Tooltip";
import Fab from "@mui/material/Fab";
import Zoom from "@mui/material/Zoom";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import Drawer from "@mui/material/Drawer";
import Reports from "./Reports";
import { useSnackbar } from "notistack";
import { useLocation, Redirect } from "react-router-dom";
import { setAlarmsFilter } from "rtkSlices/AlarmsFilterSlice";

export default function AlarmsDashboard(props) {
  const location=useLocation()
  let token = window.localStorage.getItem("token");
  let priorities = ["CRITICAL", "MAJOR", "MINOR", "WARNING"];
  let statuses = ["ACTIVE", "ACKNOWLEDGED", "CLEARED"];
  const metaDataValue = useSelector((state) => state.metaData);
  const alarmsFilter = useSelector((state) => state.alarmsFilter);
  // Scheduled-Reports gate. ⊤ (super-admin) always sees it; otherwise the
  // permission comes from the Solution Features > Scheduled Reports master
  // (undefined => not granted => FAB + Drawer hidden entirely). Two-level
  // optional chaining tolerates a role with no Solution Features row at all.
  const schedReportsPerm = metaDataValue.unscopedAdmin
    ? "ALL"
    : metaDataValue.modulePermissions?.["Solution Features"]?.["Scheduled Reports"];
  // Page gate: the alarms dashboard belongs to the Solution Features > Alarms
  // master. Non-holders are sent back to the catalogue (never a blank page).
  // Only gate once the role has actually loaded (apps is undefined until then).
  const holdsAlarmsFeature =
    metaDataValue.unscopedAdmin ||
    ["ALL", "READ"].includes(
      metaDataValue.modulePermissions?.["Solution Features"]?.Alarms
    );
  const roleLoaded = !!metaDataValue.apps;
  // console.log({alarmsFilter})
  const [csvData, setCsvData] = React.useState([]);
  const [allAlarms, setAllAlarms] = React.useState([]);
  const [drawer, setDrawer] = React.useState(false);
  const dispatch=useDispatch()

  // Resolve the "originating" solution context once. Two consumers:
  //   1. mount effect — seed the filter dropdown's default
  //   2. Filters back button — where "close" returns to
  // ESB writes a group id (not service id) into localStorage.currentService;
  // the group branch preserves back-nav for those users but falls back to "All"
  // for the dropdown since a group spans multiple services.
  const origin = useMemo(() => {
    const fromState = location.state?.selectedSolution;
    if (fromState?.id && metaDataValue.services.find((s) => s.id === fromState.id)) {
      return { name: fromState.name, backUrl: `/solutions/${fromState.id}` };
    }
    const ls = window.localStorage.getItem("currentService");
    if (ls) {
      const svc = metaDataValue.services.find((s) => s.id === ls);
      if (svc) {
        return { name: svc.name, backUrl: `/solutions/${svc.id}` };
      }
      if (metaDataValue.services.some((s) => s.group?.id === ls)) {
        return { name: null, backUrl: `/solutions/${ls}` };
      }
    }
    return null;
  }, [location.state, metaDataValue.services]);

  useEffect(() => {
    dispatch(setAlarmsFilter({
      ...alarmsFilter,
      solutions: origin?.name ? [origin.name] : ['All'],
    }));
  }, [origin?.name]);

  // Stale-selection guard. If the dropdown holds a solution name that's no
  // longer in metaDataValue.services (deleted/renamed mid-session, or a
  // stale URL/state), reset to "All" so the dashboard scope matches the UI.
  // Pairs with the defensive fall-through in each component's
  // buildServiceIdParam so the request stays RBAC-scoped either way.
  useEffect(() => {
    const sel = alarmsFilter.solutions?.[0];
    if (
      sel &&
      sel !== "All" &&
      metaDataValue.services?.length &&
      !metaDataValue.services.some((s) => s.name === sel)
    ) {
      dispatch(setAlarmsFilter({ ...alarmsFilter, solutions: ["All"] }));
      enqueueSnackbar(
        {
          title: "Alarms",
          message: `Solution "${sel}" is no longer accessible. Switched to All Solutions.`,
          variant: "warning",
        },
        { timeOut: 3000 }
      );
    }
  }, [alarmsFilter.solutions, metaDataValue.services]);

  useEffect(()=>{
    //resetting form on page unmount

    const reset=()=>{
      dispatch(setAlarmsFilter({
        date: {
          startTime: new Date().setDate(new Date().getDate() - 7),
          endTime: new Date().setDate(new Date().getDate())
        },
        status: ["ACTIVE","ACKNOWLEDGED"],
        solutions: ['All'],
        priority: [],
        emails: false,
        actuations: false,
        search: {
          asset: "",
          rule: ""
        }
      }))
    }


    return ()=>{
      reset()
    }
  },[])



  const { enqueueSnackbar } = useSnackbar();
  const devices = useGetDevicesQuery(
    {
      token,
      group: "",
      params: `&search=${
        alarmsFilter.search.asset
      }&searchFields=${JSON.stringify(["name"])}`,
    },
    { skip: !alarmsFilter.search.asset }
  );

  // Asset-name search resolves to a device list via the `devices` query.
  // Derive sensorIds straight from the query result — no separate state —
  // so it can never go stale. In particular, when a search matches zero
  // devices this is [], not the previous search's ids (the old state-based
  // code never reset it, silently leaving the list filtered by the wrong
  // devices). Children gate their alarm queries on `loading`
  // (= devices.isFetching), so they don't fire mid-resolution.
  const deviceSearchActive = !!alarmsFilter.search.asset;
  const sensorIds =
    deviceSearchActive && devices.isSuccess && !devices.isFetching
      ? (devices.data?.payload?.data || []).map((d) => d.internalId)
      : [];

  useEffect(() => {
    if (
      deviceSearchActive &&
      devices.isSuccess &&
      !devices.isFetching &&
      !devices.data?.payload?.data?.length
    ) {
      showSnackbar("Devices", "No devices found", "error", 1000);
    }
  }, [devices.isFetching]);

  function showSnackbar(title, message, variant, timeOut) {
    return enqueueSnackbar(
      { title, message: message ? message : "Something went wrong", variant },
      { timeOut }
    );
  }

  function generateRows(data) {
    // Guard against an absent payload. When the alarms query returns an
    // empty/short-shaped payload (e.g. no devices matched the filter),
    // `data` can be undefined; `JSON.stringify(undefined)` yields the value
    // `undefined`, and `JSON.parse(undefined)` throws
    // `SyntaxError: "undefined" is not valid JSON`.
    if (!Array.isArray(data)) return [];
    let tempData = JSON.parse(JSON.stringify(data));
    tempData.forEach((r) => {
      r.service =
        alarmsFilter.solutions[0] == "All"
          ? metaDataValue.services.find((s) => s.id == r.serviceId)?.name
          : alarmsFilter.solutions[0];
      // Leave createdAt/updatedAt as the raw ISO value here. The List table
      // renders every cell through newTable.js `sortDate`, which parses the
      // value and formats it en-GB for display — pre-formatting to a
      // DD/MM/YYYY string here would make that `new Date(...)` mis-parse it as
      // US M/D/YYYY (day/month swap). The CSV export formats these fields
      // explicitly in generateExportData below.
      if (isJsonString(r.text) != r.text) {
        r.text = isJsonString(r.text);
        // Prefer the explicit `reason` field on the parsed payload when
        // present (e.g. geofence entry/exit alarms where the human text
        // sits there and there's no sensor/reading). Fall back to the
        // assembled "<sensor> (value: X) is <condition> - <threshold>"
        // shape, but skip each segment whose inputs are undefined so
        // we never produce "(value: undefined)" or " - undefined" noise.
        const hasReading =
          r.text?.sensorFriendlyName !== undefined &&
          r.text?.sensorFriendlyName !== null &&
          r.text?.reading !== undefined &&
          r.text?.reading !== null;
        const readingSegment = hasReading
          ? `${r.text.sensorFriendlyName} (value: ${r.text.reading})`
          : "";
        const conditionSegment =
          r.text?.condition !== undefined && r.text?.condition !== null
            ? `${readingSegment ? " is " : ""}${r.text.condition}`
            : "";
        const thresholdSegment =
          r.text?.threshold !== undefined && r.text?.threshold !== null
            ? ` - ${r.text.threshold}`
            : "";
        const assembled = (readingSegment + conditionSegment + thresholdSegment).trim();
        r.reason = r.text.reason || assembled || "No details available";
        r.email = r.text.emails;
        if (r.text.actions && r.text.actions.length && r.text.actuatorNames) {
          r.actuations = [];
          r.text.actions.forEach((a) => {
            r.actuations.push(
              `${r.text.actuatorNames[a.actuatorId]} → ${a.commandLabel}`
            );
          });
        }
      } else {
        r.reason = r.text;
      }
    });
    return tempData;
  }

  function generateExportData(data) {
    if (!Array.isArray(data)) {
      setCsvData([]);
      return;
    }
    let tempColumns = [
      { id: "service", label: "Solution", align: "center" },
      { id: "deviceName", label: "Asset", align: "center" },
      { id: "severity", label: "Priority", align: "center" },
      { id: "status", label: "Status", align: "center" },
      {
        // "Count" is the alarm's LIFETIME repeat counter (since Created At),
        // maintained by Cumulocity dedup on every re-raise. It is NOT scoped
        // to the dashboard's date filter - label it so a week-filtered view
        // isn't read as "N occurrences this week".
        id: "count", label: "Count (since created)", align: "center" },
      { id: "reason", label: "Reason", align: "center" },
      { id: "email", label: "Emails", align: "center" },
      { id: "actuations", label: "Actuations", align: "center" },
      { id: "type", label: "Type", align: "center" },
      { id: "createdAt", label: "Created At", align: "center" },
      { id: "updatedAt", label: "Updated At", align: "center" },
    ];
    let keys = tempColumns.map((c) => c.id);
    data.forEach((t) => {
      Object.keys(t).forEach((t2) => {
        if (!keys.includes(t2)) {
          delete t[t2];
        }
      });
    });
    // Fields whose value is a raw ISO date and must be rendered en-GB
    // (DD/MM/YYYY HH:mm:ss) in the export — matching what the List shows and
    // avoiding the browser-locale-dependent US M/D/YYYY.
    const dateFields = ["createdAt", "updatedAt"];
    const fmtCsv = (id, val) => {
      if (!dateFields.includes(id) || !val) return val;
      const dt = new Date(val);
      return isNaN(dt)
        ? val
        : `${dt.toLocaleDateString("en-GB")} ${dt.toLocaleTimeString("en-GB")}`;
    };
    let tempCsvData = [];
    tempCsvData.push(tempColumns.map((t) => t.label));
    data.forEach((t1, i) => {
      tempCsvData[0].forEach((t2, j) => {
        if (!tempCsvData[i + 1]) {
          tempCsvData[i + 1] = [];
        }
        const col = tempColumns.find((c) => c.label == t2);
        tempCsvData[i + 1].push(fmtCsv(col.id, t1[col.id]));
      });
    });
    setCsvData(tempCsvData);
  }

  function isJsonString(str) {
    let out;
    try {
      out = JSON.parse(str);
    } catch (e) {
      return str;
    }
    return out;
  }

  const priorityChart = () => {
    return (
      <Count
        type="priorities"
        setAllAlarms={setAllAlarms}
        generateRows={generateRows}
        generateExportData={generateExportData}
        loading={devices.isFetching}
        sensorIds={!alarmsFilter.search.asset ? [] : sensorIds}
        serviceId={props.serviceId}
      />
    );
  };

  const statusChart = () => {
    return (
      <Count
        type="status"
        setAllAlarms={setAllAlarms}
        generateRows={generateRows}
        generateExportData={generateExportData}
        loading={devices.isFetching}
        sensorIds={!alarmsFilter.search.asset ? [] : sensorIds}
        serviceId={props.serviceId}
      />
    );
  };

  const toggleDrawer = () => {
    setDrawer(!drawer);
  };

  if (roleLoaded && !holdsAlarmsFeature) {
    return <Redirect to="/solutions/catalogue" />;
  }
  // Report scoping: the popup offers only SR-eligible solutions ("All" = that
  // set). ⊤ sees everything; tolerant-absent keeps pre-migration roles whole.
  const srSolutions = metaDataValue.unscopedAdmin
    ? metaDataValue.services
    : metaDataValue.services.filter(
        (s) => metaDataValue.srEligible?.[String(s.id)] !== false
      );
  return (
    <>
      {schedReportsPerm && (
        <>
          <Drawer anchor={"right"} open={drawer} onClose={toggleDrawer}>
            <div style={{ margin: "20px 20px 10px 20px", overflow: "hidden" }}>
              <Reports
                solutions={[{ name: "All", id: "All" }, ...srSolutions]}
                sensorIds={!alarmsFilter.search.asset ? [] : sensorIds}
                permission={schedReportsPerm}
              />
            </div>
          </Drawer>
          <Dragable bottom={"30px"} right={"30px"} name="add-operations">
            <Tooltip
              title="Scheduled Reports"
              placement="left"
              arrow
              TransitionComponent={Zoom}
            >
              <Fab
                style={{ boxShadow: "none" }}
                color="secondary"
                onClick={toggleDrawer}
              >
                <ScheduleOutlinedIcon />
              </Fab>
            </Tooltip>
          </Dragable>
        </>
      )}
      <div style={{ overflow: "hidden" }}>
        <AlarmFilters
          priorities={priorities}
          statuses={statuses}
          solutions={[{ name: "All", id: "All" }, ...metaDataValue.services]}
          filters={ alarmsFilter }
          serviceId={props.serviceId}
          origin={origin}
        />
        <div style={{ display: "flex" }}>
          {priorityChart()}
          {statusChart()}
          <Trend
            loading={devices.isFetching}
            sensorIds={!alarmsFilter.search.asset ? [] : sensorIds}
            serviceId={props.serviceId}
          />
        </div>
        <div
          style={{
            backgroundColor: "white",
            paddingBottom: "1px",
            height: "calc(100vh - 408px)",
            borderRadius: "20px",
          }}
        >
          <List
            allAlarms={allAlarms}
            generateRows={generateRows}
            csvData={csvData}
            loading={devices.isFetching}
            sensorIds={!alarmsFilter.search.asset ? [] : sensorIds}
            serviceId={props.serviceId}
          />
        </div>
      </div>
    </>
  );
}
